/**
 * AlertePresetsPage — modèles d'alerte municipale (item 8.8, migration 233).
 *
 * « Un clic → alerte + destinataires pré-remplis (ex. orage : vérifiez vos
 * affichages, DUERP). »
 *
 * Un modèle fige ce qui ne change pas d'un épisode à l'autre : le niveau, la
 * catégorie, le texte, les canaux et les secteurs destinataires. Il n'envoie
 * RIEN — il pré-remplit l'écran de création, où l'agent relit et corrige.
 *
 * Deux partis pris repris des référentiels RH (mig 232) :
 *   · désactiver, jamais supprimer — un modèle retiré reste lisible dans
 *     l'audit des alertes qui en sont issues ;
 *   · cet écran montre AUSSI les modèles désactivés (`tous=1`), barrés, sans
 *     quoi on ne pourrait jamais les réactiver.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { Button, Badge, Card, Alert, Field, LogoMark, SectionEyebrow } from '@/components/ui';
import { GrcLayout } from '@/components/grc/GrcLayout';
import {
  Bell, BellPlus, ArrowLeft, AlertCircle, Loader2, Check, X, Info,
} from 'lucide-react';
import type {
  GrcAlertePreset, GrcAlerteNiveau, GrcAlerteCanaux,
} from '@roronoa-collectivites/shared-types';

const INPUT_CLS = 'h-10 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink shadow-soft transition-all focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong';
const TEXTAREA_CLS = 'w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass p-2.5 text-sm text-ink shadow-soft transition-all focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong';

const NIVEAUX: Array<{ value: GrcAlerteNiveau; label: string }> = [
  { value: 'info',   label: 'Information' },
  { value: 'avis',   label: 'Avis' },
  { value: 'alerte', label: 'Alerte' },
  { value: 'crise',  label: 'Crise' },
];

const NIVEAU_BADGE: Record<GrcAlerteNiveau, 'success' | 'warning' | 'urgent' | 'neutral'> = {
  info: 'success', avis: 'warning', alerte: 'urgent', crise: 'neutral',
};

const CATEGORIES = [
  { value: 'meteo',      label: 'Météo' },
  { value: 'securite',   label: 'Sécurité' },
  { value: 'sanitaire',  label: 'Sanitaire' },
  { value: 'voirie',     label: 'Voirie' },
  { value: 'reseau',     label: 'Réseaux (eau, électricité)' },
  { value: 'evenement',  label: 'Événement' },
  { value: 'exercise',   label: 'Exercice' },
  { value: 'autre',      label: 'Autre' },
];

const CANAUX: Array<{ cle: keyof GrcAlerteCanaux; label: string; porteFichier: boolean }> = [
  { cle: 'site',    label: 'Site',    porteFichier: true  },
  { cle: 'mail',    label: 'Mail',    porteFichier: true  },
  { cle: 'pushPwa', label: 'Push',    porteFichier: false },
  { cle: 'sms',     label: 'SMS',     porteFichier: false },
  { cle: 'panneau', label: 'Panneau', porteFichier: false },
];

const CANAUX_PAR_NIVEAU: Record<GrcAlerteNiveau, GrcAlerteCanaux> = {
  info:   { site: true, mail: true, pushPwa: false, sms: false, panneau: false },
  avis:   { site: true, mail: true, pushPwa: true,  sms: false, panneau: false },
  alerte: { site: true, mail: true, pushPwa: true,  sms: true,  panneau: false },
  crise:  { site: true, mail: true, pushPwa: true,  sms: true,  panneau: true  },
};

export function AlertePresetsPage() {
  const { user } = useAuth();
  const canManage = ['super_admin', 'admin', 'manager'].includes(user?.role ?? '');

  const [presets, setPresets] = useState<GrcAlertePreset[]>([]);
  const [secteurs, setSecteurs] = useState<Array<{ id: string; libelle: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formOuvert, setFormOuvert] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [libelle, setLibelle] = useState('');
  const [niveau, setNiveau] = useState<GrcAlerteNiveau>('avis');
  const [categorie, setCategorie] = useState('meteo');
  const [titre, setTitre] = useState('');
  const [message, setMessage] = useState('');
  const [messageLong, setMessageLong] = useState('');
  const [zone, setZone] = useState('');
  const [secteurIds, setSecteurIds] = useState<string[]>([]);
  const [canaux, setCanaux] = useState<GrcAlerteCanaux>(CANAUX_PAR_NIVEAU.avis);
  const [canauxTouches, setCanauxTouches] = useState(false);

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // `tous` : les désactivés doivent rester visibles pour être réactivés.
      setPresets((await api.grc.alertes.presets.list(true)).presets);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Chargement impossible');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { void charger(); }, [charger]);

  useEffect(() => {
    void (async () => {
      try {
        setSecteurs((await api.grc.sms.secteurs.list()).secteurs
          .map((s) => ({ id: s.id, libelle: s.libelle })));
      } catch { /* la segmentation est optionnelle */ }
    })();
  }, []);

  // Tant que l'utilisateur n'a pas touché aux canaux, ils suivent le niveau.
  useEffect(() => {
    if (!canauxTouches) setCanaux(CANAUX_PAR_NIVEAU[niveau]);
  }, [niveau, canauxTouches]);

  function reinitialiser() {
    setLibelle(''); setNiveau('avis'); setCategorie('meteo');
    setTitre(''); setMessage(''); setMessageLong(''); setZone('');
    setSecteurIds([]); setCanaux(CANAUX_PAR_NIVEAU.avis); setCanauxTouches(false);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true); setError(null); setSuccess(null);
    try {
      await api.grc.alertes.presets.creer({
        libelle: libelle.trim(),
        niveau,
        categorie,
        titre: titre.trim() || undefined,
        message: message.trim() || undefined,
        messageLong: messageLong.trim() || undefined,
        zoneGeographique: zone.trim() || undefined,
        secteurIds: secteurIds.length ? secteurIds : undefined,
        canaux,
      });
      setSuccess('Modèle créé');
      setFormOuvert(false);
      reinitialiser();
      await charger();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Création impossible');
    } finally { setSubmitting(false); }
  }

  async function basculerActif(preset: GrcAlertePreset) {
    setError(null); setSuccess(null);
    try {
      await api.grc.alertes.presets.maj(preset.id, { actif: !preset.actif });
      await charger();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Modification impossible');
    }
  }

  return (
    <GrcLayout active="communication">
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <LogoMark icon={Bell} accent="grc" size="md" />
            <div>
              <SectionEyebrow as="span">Alertes municipales</SectionEyebrow>
              <h1 className="text-xl font-semibold text-ink">Modèles d'alerte</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/grc/alertes"
              className="inline-flex items-center gap-1.5 rounded-r-md px-1 text-xs text-ink-muted transition-colors hover:text-grc-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
            >
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Retour aux alertes
            </Link>
            {canManage && !formOuvert && (
              <Button type="button" variant="primary" size="sm" onClick={() => setFormOuvert(true)}>
                <BellPlus className="h-4 w-4" aria-hidden="true" /> Nouveau modèle
              </Button>
            )}
          </div>
        </div>

        {error && <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>}
        {success && <Alert variant="success" icon={Check} className="mb-3">{success}</Alert>}

        <Alert variant="info" icon={Info} className="mb-4">
          Un modèle <strong>ne déclenche rien</strong>. Il remplit le formulaire de
          création — niveau, texte, canaux et secteurs destinataires — puis l'agent
          relit et crée un brouillon. Le déclenchement garde sa double validation.
        </Alert>

        {formOuvert && canManage && (
          <Card variant="glass" padded className="mb-4">
            <SectionEyebrow as="h2" className="mb-3">Nouveau modèle</SectionEyebrow>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Nom du modèle *" htmlFor="preset-libelle">
                  <input
                    id="preset-libelle"
                    value={libelle}
                    onChange={(e) => setLibelle(e.target.value)}
                    required minLength={2} maxLength={120}
                    placeholder="Ex : Orage — consignes affichages"
                    className={INPUT_CLS}
                  />
                </Field>
                <Field label="Zone géographique" htmlFor="preset-zone">
                  <input
                    id="preset-zone"
                    value={zone}
                    onChange={(e) => setZone(e.target.value)}
                    maxLength={300}
                    placeholder="Ex : Toute la commune"
                    className={INPUT_CLS}
                  />
                </Field>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Niveau *" htmlFor="preset-niveau">
                  <select
                    id="preset-niveau" value={niveau}
                    onChange={(e) => setNiveau(e.target.value as GrcAlerteNiveau)}
                    className={INPUT_CLS}
                  >
                    {NIVEAUX.map((n) => <option key={n.value} value={n.value}>{n.label}</option>)}
                  </select>
                </Field>
                <Field label="Catégorie *" htmlFor="preset-categorie">
                  <select
                    id="preset-categorie" value={categorie}
                    onChange={(e) => setCategorie(e.target.value)}
                    className={INPUT_CLS}
                  >
                    {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </Field>
              </div>

              <Field label="Titre pré-rempli" htmlFor="preset-titre">
                <input
                  id="preset-titre" value={titre}
                  onChange={(e) => setTitre(e.target.value)} maxLength={200}
                  placeholder="Laisser vide pour l'écrire sur le moment"
                  className={INPUT_CLS}
                />
              </Field>

              <Field label="Message court (SMS, push)" htmlFor="preset-message">
                <textarea
                  id="preset-message" value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={500} rows={2} className={TEXTAREA_CLS}
                />
              </Field>

              <Field label="Message long (site, mail)" htmlFor="preset-message-long">
                <textarea
                  id="preset-message-long" value={messageLong}
                  onChange={(e) => setMessageLong(e.target.value)}
                  maxLength={20000} rows={4} className={TEXTAREA_CLS}
                />
              </Field>

              {/* Canaux */}
              <fieldset>
                <legend className="mb-2 text-sm font-medium text-ink">Canaux pré-cochés</legend>
                <div className="flex flex-wrap gap-2">
                  {CANAUX.map((c) => {
                    const actif = canaux[c.cle];
                    return (
                      <button
                        key={c.cle}
                        type="button"
                        aria-pressed={actif}
                        onClick={() => {
                          setCanauxTouches(true);
                          setCanaux({ ...canaux, [c.cle]: !actif });
                        }}
                        className={[
                          'inline-flex items-center gap-1.5 rounded-r-lg border px-3 py-1.5 text-sm transition-all',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
                          actif
                            ? 'border-grc/50 bg-grc/10 font-medium text-ink'
                            : 'border-border bg-surface/40 text-ink-muted hover:border-grc/40',
                        ].join(' ')}
                      >
                        {actif ? <Check className="h-3.5 w-3.5" aria-hidden="true" />
                               : <X className="h-3.5 w-3.5" aria-hidden="true" />}
                        {c.label}
                      </button>
                    );
                  })}
                </div>
                {canaux.sms && (
                  <Alert variant="warning" icon={AlertCircle} className="mt-2">
                    Le SMS est un canal payant. Un modèle qui le pré-coche transforme
                    un clic en dépense — vérifiez que c'est voulu.
                  </Alert>
                )}
              </fieldset>

              {/* Destinataires */}
              {secteurs.length > 0 && (
                <fieldset>
                  <legend className="mb-2 text-sm font-medium text-ink">
                    Secteurs destinataires
                  </legend>
                  <p className="mb-2 text-2xs text-ink-muted">
                    Aucun secteur coché = tous les abonnés.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {secteurs.map((s) => {
                      const actif = secteurIds.includes(s.id);
                      return (
                        <button
                          key={s.id}
                          type="button"
                          aria-pressed={actif}
                          onClick={() => setSecteurIds(actif
                            ? secteurIds.filter((x) => x !== s.id)
                            : [...secteurIds, s.id])}
                          className={[
                            'rounded-r-lg border px-3 py-1.5 text-sm transition-all',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
                            actif
                              ? 'border-grc/50 bg-grc/10 font-medium text-ink'
                              : 'border-border bg-surface/40 text-ink-muted hover:border-grc/40',
                          ].join(' ')}
                        >
                          {s.libelle}
                        </button>
                      );
                    })}
                  </div>
                </fieldset>
              )}

              <div className="flex items-center gap-2 pt-1">
                <Button type="submit" variant="primary" size="sm" disabled={submitting}>
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                  Créer le modèle
                </Button>
                <Button
                  type="button" variant="ghost" size="sm"
                  onClick={() => { setFormOuvert(false); reinitialiser(); }}
                >
                  Annuler
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Liste */}
        <Card variant="glass" padded>
          <SectionEyebrow as="h2" className="mb-3">Modèles de la commune</SectionEyebrow>

          {loading ? (
            <p className="flex items-center gap-2 text-sm text-ink-muted">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Chargement…
            </p>
          ) : presets.length === 0 ? (
            <div className="py-6 text-center">
              <LogoMark icon={Bell} accent="grc" size="md" className="mx-auto mb-3" />
              <p className="text-sm text-ink-muted">
                Aucun modèle pour l'instant. Créez ceux que votre commune répète —
                orage, coupure d'eau, route barrée.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {presets.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`text-sm font-medium text-ink ${p.actif ? '' : 'line-through opacity-60'}`}>
                        {p.libelle}
                      </span>
                      <Badge variant={NIVEAU_BADGE[p.niveau]} size="sm">
                        {NIVEAUX.find((n) => n.value === p.niveau)?.label ?? p.niveau}
                      </Badge>
                      {!p.actif && <Badge variant="neutral" size="sm">Désactivé</Badge>}
                      {p.canaux.sms && <Badge variant="warning" size="sm">SMS</Badge>}
                    </div>
                    <p className="mt-0.5 text-2xs text-ink-muted">
                      {CANAUX.filter((c) => p.canaux[c.cle]).map((c) => c.label).join(' · ') || 'Aucun canal'}
                      {' — '}
                      {p.secteurIds.length === 0
                        ? 'tous les abonnés'
                        : `${p.secteurIds.length} secteur${p.secteurIds.length > 1 ? 's' : ''}`}
                    </p>
                  </div>
                  {canManage && (
                    <Button
                      type="button" variant="ghost" size="sm"
                      onClick={() => void basculerActif(p)}
                    >
                      {p.actif ? 'Désactiver' : 'Réactiver'}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </GrcLayout>
  );
}
